import { groupBy } from "diagnostics-lib/iterable";

export type DraftFormulaVersion = {
  localId: string;
  persistedId: string | null;
  quantityId: string;
  formula: string;
  startsAt: Date;
};

export function getFormulaLatestVersion<
  FormulaVersion extends { quantityId: string; startsAt: Date },
>(quantityId: string, formulaVersions: FormulaVersion[]) {
  /** @note we can implement WeakMap cache strategy for optimization */
  const index = groupBy(
    Array.from(formulaVersions).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime()),
    "quantityId",
  );
  return index.get(quantityId)?.at(0) ?? null;
}

export function getCurrentFormulaVersion<
  FormulaVersion extends { quantityId: string; startsAt: Date },
>(quantityId: string, formulaVersions: FormulaVersion[]) {
  const index = groupBy(
    Array.from(formulaVersions).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime()),
    "quantityId",
  );
  return (
    index
      .get(quantityId)
      ?.filter(v => v.startsAt.getTime() <= Date.now())
      .at(0) ?? null
  );
}
export function getFormulaVersionAt<FormulaVersion extends { quantityId: string; startsAt: Date }>(
  quantityId: string,
  formulaVersions: FormulaVersion[],
  date: Date,
) {
  const index = groupBy(
    Array.from(formulaVersions).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime()),
    "quantityId",
  );
  return (
    index
      .get(quantityId)
      ?.filter(v => v.startsAt.getTime() <= date.getTime())
      .at(0) ?? null
  );
}

export function getFormulaVersionApplicationPeriod<
  FormulaVersion extends { id: string; startsAt: Date },
>(formulaVersion: FormulaVersion, formulaVersions: FormulaVersion[]) {
  const formulaVersionsAsc = Array.from(formulaVersions).sort(
    (a, b) => a.startsAt.getTime() - b.startsAt.getTime(),
  );
  const i = formulaVersionsAsc.findIndex(fv => fv.id === formulaVersion.id);
  if (i < 0) return null;
  return {
    from: formulaVersion.startsAt,
    to: formulaVersionsAsc.at(i + 1)?.startsAt ?? null,
  };
}

export function getBaseFormulaApplicationPeriod<FormulaVersion extends { startsAt: Date }>(
  startsAt: Date,
  formulaVersions: FormulaVersion[],
) {
  const formulaVersionsAsc = Array.from(formulaVersions).sort(
    (a, b) => a.startsAt.getTime() - b.startsAt.getTime(),
  );
  return { from: startsAt, to: formulaVersionsAsc.at(0)?.startsAt ?? new Date() };
}

export function initializeDraftFormulaVersions<
  FormulaVersion extends {
    id: string;
    quantityId: string;
    formula: string;
    startsAt: Date;
  },
>(formulaVersions: FormulaVersion[]): DraftFormulaVersion[] {
  return formulaVersions.map(version => ({
    localId: version.id,
    persistedId: version.id,
    quantityId: version.quantityId,
    formula: version.formula,
    startsAt: version.startsAt,
  }));
}

export function createDraftFormulaVersion({
  quantityId,
  startsAt,
}: {
  quantityId: string;
  startsAt?: Date;
}): DraftFormulaVersion {
  return {
    localId: `draft-${crypto.randomUUID()}`,
    persistedId: null,
    quantityId,
    formula: "",
    startsAt: startsAt ?? new Date(),
  };
}

export function sortDraftFormulaVersions(formulaVersions: DraftFormulaVersion[]) {
  return [...formulaVersions].sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export function getDuplicateDraftFormulaVersionLocalIds(
  formulaVersions: Pick<DraftFormulaVersion, "localId" | "startsAt">[],
) {
  const localIdsByStartsAt = new Map<number, string[]>();

  for (const version of formulaVersions) {
    const startsAtTime = version.startsAt.getTime();
    const existingLocalIds = localIdsByStartsAt.get(startsAtTime);

    if (existingLocalIds === undefined) {
      localIdsByStartsAt.set(startsAtTime, [version.localId]);
      continue;
    }

    existingLocalIds.push(version.localId);
  }

  const duplicatedLocalIds = new Set<string>();

  for (const localIds of localIdsByStartsAt.values()) {
    if (localIds.length < 2) continue;

    for (const localId of localIds) duplicatedLocalIds.add(localId);
  }

  return duplicatedLocalIds;
}

export function deleteDraftFormulaVersion(
  formulaVersions: DraftFormulaVersion[],
  localId: string,
): DraftFormulaVersion[] {
  return formulaVersions.filter(version => version.localId !== localId);
}

export function updateDraftFormulaVersionFormula(
  formulaVersions: DraftFormulaVersion[],
  localId: string,
  formula: string,
): DraftFormulaVersion[] {
  return formulaVersions.map(version =>
    version.localId === localId ? { ...version, formula } : version,
  );
}

export function updateDraftFormulaVersionStartsAt(
  formulaVersions: DraftFormulaVersion[],
  localId: string,
  startsAt: Date,
): DraftFormulaVersion[] {
  return formulaVersions.map(version =>
    version.localId === localId ? { ...version, startsAt } : version,
  );
}

export function areFormulaVersionsEqual(
  a: Pick<DraftFormulaVersion, "formula" | "startsAt">,
  b: Pick<DraftFormulaVersion, "formula" | "startsAt">,
) {
  return a.formula === b.formula && a.startsAt.getTime() === b.startsAt.getTime();
}

export function hasFormulaVersionDraftChanges({
  deletedFormulaVersionIds,
  draftFormulaVersions,
  persistedFormulaVersionsById,
}: {
  deletedFormulaVersionIds: string[];
  draftFormulaVersions: DraftFormulaVersion[];
  persistedFormulaVersionsById: Map<string, Pick<DraftFormulaVersion, "formula" | "startsAt">>;
}) {
  return (
    deletedFormulaVersionIds.length > 0 ||
    draftFormulaVersions.some(version => {
      if (version.persistedId === null) return true;

      const persistedVersion = persistedFormulaVersionsById.get(version.persistedId);
      return persistedVersion ? !areFormulaVersionsEqual(version, persistedVersion) : true;
    })
  );
}

export function buildFormulaVersionMutations({
  deletedFormulaVersionIds,
  draftFormulaVersions,
  persistedFormulaVersionsById,
}: {
  deletedFormulaVersionIds: string[];
  draftFormulaVersions: DraftFormulaVersion[];
  persistedFormulaVersionsById: Map<string, Pick<DraftFormulaVersion, "formula" | "startsAt">>;
}) {
  const create = draftFormulaVersions
    .filter(version => version.persistedId === null)
    .map(version => ({
      startsAt: version.startsAt,
      formula: version.formula,
    }));

  const update = draftFormulaVersions
    .filter(
      version =>
        version.persistedId !== null && !deletedFormulaVersionIds.includes(version.persistedId),
    )
    .map(version => {
      const persistedVersion = persistedFormulaVersionsById.get(version.persistedId ?? "");
      if (!persistedVersion || areFormulaVersionsEqual(version, persistedVersion)) return null;

      return {
        id: version.persistedId ?? "",
        startsAt: version.startsAt,
        formula: version.formula,
      };
    })
    .filter(
      (version): version is { id: string; startsAt: Date; formula: string } => version !== null,
    );

  const del = deletedFormulaVersionIds;

  return {
    create,
    update,
    delete: del,
    hasMutations: create.length > 0 || update.length > 0 || del.length > 0,
  };
}
export function mapFormulaVersionStartsAtToDate<FormulaVersion extends { startsAt: string }>(
  formulaVersions: FormulaVersion[],
): (Omit<FormulaVersion, "startsAt"> & { startsAt: Date })[] {
  return formulaVersions.map(fv => ({ ...fv, startsAt: new Date(fv.startsAt) }));
}
